import React, { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { User, Settings, CreditCard } from 'lucide-react'
import PersonalSettingsTab from './components/PersonalSettingsTab'
import BankSettingsTab from './components/BankSettingsTab'
import SystemSettingsTab from './components/SystemSettingsTab'

type TabType = 'personal' | 'bank' | 'system'

const SettingsPage: React.FC = () => {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<TabType>('personal')

  // 根據用戶角色決定是否顯示系統設定分頁
  const showSystemTab = user?.role === 'ADMIN'

  const tabs = [
    {
      id: 'personal' as TabType,
      name: '個人設定',
      icon: User,
      description: '管理您的個人資料和偏好設定'
    },
    {
      id: 'bank' as TabType,
      name: '銀行資料',
      icon: CreditCard,
      description: '管理您的銀行匯款資料'
    },
    ...(showSystemTab ? [{
      id: 'system' as TabType,
      name: '系統設定',
      icon: Settings,
      description: '管理系統全域設定（僅管理員）'
    }] : [])
  ]

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-secondary-600 dark:text-secondary-400">載入中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gradient mb-2">設定</h1>
        <p className="text-secondary-600">
          管理您的個人資料{showSystemTab ? '和系統設定' : ''}
        </p>
      </div>
      
      {/* 頁籤導航 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="設定分頁">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  isActive
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon
                  className={`-ml-0.5 mr-2 h-5 w-5 ${
                    isActive
                      ? 'text-primary-500'
                      : 'text-secondary-400 group-hover:text-secondary-500'
                  }`}
                />
                <span className="hidden sm:inline">{tab.name}</span>
                <span className="sm:hidden">{tab.name.substring(0, 2)}</span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* 分頁說明 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {(() => {
              const ActiveTabIcon = tabs.find(tab => tab.id === activeTab)?.icon || User
              return <ActiveTabIcon className="w-5 h-5 text-blue-600 mt-0.5" />
            })()}
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              {tabs.find(tab => tab.id === activeTab)?.name}
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              {tabs.find(tab => tab.id === activeTab)?.description}
            </p>
          </div>
        </div>
      </div>

      {/* 分頁內容 */}
      <div className="mt-8">
        {activeTab === 'personal' && <PersonalSettingsTab />}
        {activeTab === 'bank' && <BankSettingsTab />}
        {activeTab === 'system' && <SystemSettingsTab />}
      </div>
    </div>
  )
}

export default SettingsPage
